import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CheckCircle2, CircleAlert, Film, Users } from "lucide-react";
import { tr, CHECKIN_METHOD_LABEL } from "@/lib/labels";
import { ANALYTICS_COLORS, ChartCard, EmptyAnalytics, MetricCard, formatPercent } from "@/components/analytics/AnalyticsPrimitives";

const PRESENT = new Set(["PRESENT", "PRESENT_MANUAL"]);

export default function LessonAnalyticsSummary({ lesson, students = [], attendance = [], videoProgress = [] }) {
  if (!lesson || !["OPEN", "CLOSED"].includes(lesson.status)) return null;

  const studentIds = new Set(students.map((student) => student.id));
  const validAttendance = attendance.filter((row) => studentIds.has(row.student_id));
  const presentRecords = validAttendance.filter((row) => PRESENT.has(row.status));
  const presentIds = new Set(presentRecords.map((row) => row.student_id));
  const justifiedIds = new Set(validAttendance.filter((row) => row.status === "JUSTIFIED_ABSENCE").map((row) => row.student_id));
  const active = students.length;
  const considered = Math.max(active - justifiedIds.size, 0);
  const absent = Math.max(active - presentIds.size - justifiedIds.size, 0);
  const percent = considered > 0 ? Math.min(Math.round((presentIds.size / considered) * 100), 100) : null;

  const methods = {};
  presentRecords.forEach((row) => {
    const method = row.checkin_method || "OUTRO";
    methods[method] = (methods[method] || 0) + 1;
  });
  const methodRows = Object.entries(methods).map(([method, count]) => ({ method, count }));

  const completed = videoProgress.filter((row) => row.completed).length;
  const inProgress = videoProgress.filter((row) => !row.completed && (row.progress_percent || 0) > 0).length;

  return (
    <section className="mb-6" data-testid="lesson-analytics-summary">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.16em] text-[#6B655E] font-medium">Resumo da aula</p>
          <p className="text-sm text-[#6B655E] mt-1">Leitura rápida sem sair da operação de presença.</p>
        </div>
        <span className="text-[11px] px-2.5 py-1 rounded-full bg-[#F6EFE4] text-[#6B655E]">Analytics contextual</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <MetricCard label="Presença" value={formatPercent(percent)} icon={CheckCircle2} tone={percent != null && percent < 75 ? "critical" : "success"} hint={`${presentIds.size}/${considered} consideradas`} />
        <MetricCard label="Presentes" value={presentIds.size} icon={Users} tone="success" />
        <MetricCard label="Faltas" value={absent} icon={CircleAlert} tone={absent > 0 ? "critical" : "neutral"} hint={`${justifiedIds.size} justificada(s)`} />
        <MetricCard label="Vídeo concluído" value={videoProgress.length ? `${completed}/${videoProgress.length}` : "—"} icon={Film} tone="orange" hint={videoProgress.length ? `${inProgress} em andamento` : "Sem vídeo/progresso"} />
      </div>

      <div className="grid xl:grid-cols-2 gap-4">
        <ChartCard title="Composição da presença" subtitle="Presentes, justificadas e faltas nesta aula.">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[{ name: "Aula", present: presentIds.size, justified: justifiedIds.size, absent }]} margin={{ top: 12, right: 8, left: -18, bottom: 8 }} accessibilityLayer>
                <CartesianGrid stroke="#EEE8DE" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="present" name="Presentes" stackId="a" fill={ANALYTICS_COLORS.green} />
                <Bar dataKey="justified" name="Justificadas" stackId="a" fill={ANALYTICS_COLORS.amber} />
                <Bar dataKey="absent" name="Faltas" stackId="a" fill={ANALYTICS_COLORS.red} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="Métodos de check-in" subtitle="Como as presenças registradas chegaram nesta aula.">
          {methodRows.length ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={methodRows} margin={{ top: 12, right: 8, left: -18, bottom: 8 }} accessibilityLayer>
                  <CartesianGrid stroke="#EEE8DE" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="method" tickFormatter={(value) => tr(CHECKIN_METHOD_LABEL, value)} tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                  <Tooltip labelFormatter={(value) => tr(CHECKIN_METHOD_LABEL, value)} />
                  <Bar dataKey="count" name="Check-ins" fill={ANALYTICS_COLORS.orange} radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyAnalytics description="Nenhum check-in válido registrado nesta aula ainda." />}
        </ChartCard>
      </div>
    </section>
  );
}
